import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Anchor, Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import Spinner from '@atlaskit/spinner';

import { FallbackableImage } from '@confluence/fallbackable-image';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expVal } from '@confluence/feature-experiments';

import { ConnectAppsForMoreSuggestionsCard } from './ConnectAppsForMoreSuggestionsCard.tsx';
import { SuggestionMovePopup } from './SuggestionMovePopup.tsx';
import { SelectToggleIcon } from './SelectToggleIcon.tsx';
import { useContentTitles } from './hooks/useContentTitles.ts';
import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import { useSubmitSuggestionActions } from './hooks/useSubmitSuggestionActions.ts';
import {
	ALL_EXTERNAL_PRODUCT_CONNECTIONS,
	getSuggestionIconUrlFromContentUrl,
	getSuggestionProductKeyFromContentUrl,
	type Suggestion,
	type SuggestionGroup,
} from './__types__/RemoteObject.ts';
import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';
import { SuggestionHoverCard, GroupHoverCard } from './SuggestionHoverCard.tsx';
import { SuggestionFeedbackModal } from './SuggestionFeedbackModal.tsx';
import eyelashIcon from './img/eyelash.svg';

const SUGGESTION_PREVIEW_DWELL_MS = 500;
const PREVIEW_ITEM_COUNT = 5;

function useSuggestionPreviewAnalytics({
	spaceId,
	suggestion,
}: {
	spaceId?: string;
	suggestion: Suggestion;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hoverStartRef = useRef<number | null>(null);
	const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (previewTimeoutRef.current) {
				clearTimeout(previewTimeoutRef.current);
			}
		};
	}, []);

	const startPreviewAnalytics = useCallback(() => {
		hoverStartRef.current = Date.now();
		previewTimeoutRef.current = setTimeout(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'previewed',
					actionSubject: 'suggestion',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						suggestionId: suggestion.id,
						sourceType: getSuggestionProductKeyFromContentUrl(suggestion.url),
						previewDwellMs: Date.now() - (hoverStartRef.current ?? Date.now()),
					},
				},
			}).fire();
			previewTimeoutRef.current = null;
		}, SUGGESTION_PREVIEW_DWELL_MS);
	}, [createAnalyticsEvent, spaceId, suggestion]);

	const cancelPreviewAnalytics = useCallback(() => {
		if (previewTimeoutRef.current) {
			clearTimeout(previewTimeoutRef.current);
			previewTimeoutRef.current = null;
		}
		hoverStartRef.current = null;
	}, []);

	return { startPreviewAnalytics, cancelPreviewAnalytics };
}

const i18n = defineMessages({
	bringTogetherTitle: {
		id: 'side-navigation.review-content-suggestions-modal.bring-together-title',
		defaultMessage: 'Bring together the content that matters',
		description: 'Main title on the left panel of the content suggestions modal',
	},
	bringTogetherDescription: {
		id: 'side-navigation.review-content-suggestions-modal.bring-together-description',
		defaultMessage: 'Add these suggestions and it’ll be organized in this space.',
		description: 'Description on the left panel of the content suggestions modal',
	},
	addItemsToSpace: {
		id: 'side-navigation.review-content-suggestions-modal.add-items-to-space',
		defaultMessage: 'Add {count, plural, one {# item} other {# items}} to space',
		description: 'Button label for adding selected content suggestions to space',
	},
	expandGroup: {
		id: 'side-navigation.review-content-suggestions-modal.expand-group',
		defaultMessage: 'Expand {title}',
		description: 'Accessible label for expanding a suggestion group',
	},
	collapseGroup: {
		id: 'side-navigation.review-content-suggestions-modal.collapse-group',
		defaultMessage: 'Collapse {title}',
		description: 'Accessible label for collapsing a suggestion group',
	},
	openSuggestion: {
		id: 'side-navigation.review-content-suggestions-modal.open-suggestion',
		defaultMessage: 'Open {title} in a new tab',
		description: 'Accessible label for opening a suggestion link in a new tab',
	},
	deselectGroup: {
		id: 'side-navigation.review-content-suggestions-modal.deselect-group',
		defaultMessage: 'Deselect group {title}',
		description: 'Accessible label for deselecting all items in a suggestion group',
	},
	selectGroup: {
		id: 'side-navigation.review-content-suggestions-modal.select-group',
		defaultMessage: 'Select group {title}',
		description: 'Accessible label for selecting all items in a suggestion group',
	},
	deselectItem: {
		id: 'side-navigation.review-content-suggestions-modal.deselect-item',
		defaultMessage: 'Deselect {title}',
		description: 'Accessible label for deselecting a single suggestion item',
	},
	selectItem: {
		id: 'side-navigation.review-content-suggestions-modal.select-item',
		defaultMessage: 'Select {title}',
		description: 'Accessible label for selecting a single suggestion item',
	},
	untitled: {
		id: 'side-navigation.review-content-suggestions-modal.untitled',
		defaultMessage: 'Untitled',
		description: 'Fallback when parent content has no title',
	},
	feedbackButtonLabel: {
		id: 'side-navigation.review-content-suggestions-modal.feedback-button-label',
		defaultMessage: 'Rate suggestion quality for {title}',
		description:
			'Accessible label for the eval button that opens the suggestion quality feedback modal',
	},
	evalButtonLabel: {
		id: 'side-navigation.review-content-suggestions-modal.eval-button-label',
		defaultMessage: 'Eval',
		description: 'Label for the button that opens the suggestion quality feedback modal',
	},
	loading: {
		id: 'side-navigation.review-content-suggestions-modal.loading',
		defaultMessage: 'Loading suggestions…',
		description: 'Accessible label for the loading state in the content suggestions modal',
	},
	submitting: {
		id: 'side-navigation.review-content-suggestions-modal.submitting',
		defaultMessage: 'Adding items to space…',
		description: 'Accessible label while submitting content suggestions to the space',
	},
	spaceAvatarAlt: {
		id: 'side-navigation.review-content-suggestions-modal.space-avatar-alt',
		defaultMessage: 'Avatar for {spaceName}',
		description: 'Alternative text for the previewed space avatar in the right panel header',
	},
});

const styles = cssMap({
	layout: {
		height: '568px',
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	leftPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		paddingBlock: token('space.500'),
		paddingInline: token('space.600'),
		overflow: 'hidden',
	},
	rightPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		overflow: 'hidden',
		position: 'relative',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#8fb8f6',
		borderStartEndRadius: token('radius.xlarge'),
		borderEndEndRadius: token('radius.xlarge'),
	},
	previewPanelContent: {
		paddingBlockEnd: token('space.200'),
	},
	previewCard: {
		backgroundColor: token('color.background.input'),
		padding: token('space.150'),
	},
	previewGroupsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		borderWidth: token('border.width.focused'),
		borderStyle: 'solid',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#8fb8f6',
		borderRadius: token('radius.medium'),
		padding: token('space.050'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: 'rgb(143, 184, 246) 6px 6px 0px 0px',
	},
	previewFoldersList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	previewFolderPreview: {
		backgroundColor: token('color.background.input'),
		border: 'none',
	},
	previewFolderHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.075'),
		position: 'relative',
		transition: 'background-color 150ms ease',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: token('color.background.neutral'),
		},
	},
	previewFolderTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0%',
		flex: 1,
	},
	previewGroupTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: '0%',
		flex: 1,
	},
	previewFolderBody: {
		display: 'none',
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.150'),
	},
	previewFolderBodyExpanded: {
		display: 'block',
	},
	previewFolderBodyIndented: {
		paddingInlineStart: token('space.0'),
	},
	previewSuggestionList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.075'),
		width: '100%',
	},
	previewSuggestionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0%',
		width: '100%',
		paddingInlineStart: token('space.400'),
		paddingInlineEnd: token('space.075'),
		paddingBlock: token('space.050'),
	},
	previewSuggestionTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: '0%',
		flex: 1,
	},
	spaceHeader: {
		paddingBlock: token('space.250'),
		paddingInline: token('space.300'),
		backgroundColor: token('color.background.input'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		borderStartStartRadius: token('radius.xlarge'),
		alignItems: 'center',
		gap: token('space.100'),
		flexShrink: 0,
	},
	spaceHeaderTitle: {
		minWidth: '0%',
		flexGrow: 1,
	},
	spaceAvatarWrapper: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		flexShrink: 0,
	},
	stackedContentTypeIconWrapper: {
		marginBlockEnd: token('space.250'),
	},
	closeButtonWrapper: {
		top: token('space.100'),
		left: token('space.100'),
	},
	eyelashWrapper: {
		position: 'relative',
	},
	eyelash: {
		position: 'absolute',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		top: `calc(${token('space.negative.400')} - 16px)`,
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		left: `calc(${token('space.negative.400')} - 16px)`,
		zIndex: 100,
		animation: 'eyelashPop 400ms cubic-bezier(0.25, 0.1, 0.25, 1) forwards',
		pointerEvents: 'none',
		overflow: 'visible',
	},
	groupListScrollArea: {
		flexGrow: 1,
		overflowY: 'auto',
		scrollbarWidth: 'none',
		minHeight: '0%',
	},
	container: {
		position: 'absolute',
		top: token('space.1000'),
		left: token('space.1000'),
		right: token('space.0'),
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
		borderStartStartRadius: token('radius.xlarge'),
		overflow: 'visible',
	},
	groupCard: {
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		'&:hover': {
			borderColor: token('color.border.bold'),
		},
		transition: 'border-color 150ms ease',
	},
	groupCardExpanded: {
		borderColor: token('color.border.bold'),
		overflow: 'visible',
		position: 'relative',
		zIndex: 0,
	},
	groupCardHovering: {
		position: 'relative',
		zIndex: 700,
		borderColor: token('color.border'),
	},
	expandButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		textAlign: 'start',
		gap: token('space.100'),
		flexGrow: 1,
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.150'),
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit parent bg
			backgroundColor: 'transparent',
		},
	},
	groupTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	groupCountText: {
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	groupChevron: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
	itemsListOuter: {
		display: 'grid',
		gridTemplateRows: '0fr',
		opacity: 0,
		transition: 'grid-template-rows 350ms ease, opacity 250ms ease',
	},
	itemsListOuterExpanded: {
		gridTemplateRows: '1fr',
		opacity: 1,
	},
	itemsList: {
		overflow: 'visible',
		minHeight: '0%',
	},
	itemRowContainer: {
		position: 'relative',
		zIndex: 0,
		width: '100%',
		transition: 'background-color 150ms ease',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: token('color.background.neutral'),
		},
	},
	itemRowContainerHovered: {
		zIndex: 1,
	},
	portalHoverCard: {
		position: 'fixed',
		zIndex: 800,
		pointerEvents: 'none',
		opacity: 0,
		transform: 'translateY(8px)',
		transition: 'opacity 400ms ease, transform 400ms ease',
	},
	portalHoverCardVisible: {
		opacity: 1,
		pointerEvents: 'auto',
		transform: 'translateY(0)',
	},
	previewPlaceholderSection: {
		paddingInline: token('space.050'),
		display: 'flex',
		flexDirection: 'column',
		paddingBlock: token('space.100'),
	},
	previewPlaceholderRow: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.100'),
	},
	previewPlaceholderDot: {
		width: '10px',
		height: '10px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
		flexShrink: 0,
	},
	previewPlaceholderBarLong: {
		height: '8px',
		width: '70%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	previewPlaceholderBarFull: {
		height: '8px',
		width: '88%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	suggestionLinkWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.150'),
	},
	suggestionLink: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		minWidth: '0%',
		textDecoration: 'none',
		color: token('color.text'),
		flexGrow: 1,
		'&:hover': {
			textDecoration: 'underline',
			textDecorationColor: token('color.text'),
		},
	},
	suggestionIconSlot: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	suggestionTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	suggestionLinkExternalIcon: {
		flexShrink: 0,
	},
	suggestionExternalLinkButton: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.075'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
		color: token('color.text'),
		'&:hover': {
			textDecoration: 'none',
		},
	},
	addButtonRow: {
		paddingBlockEnd: token('space.200'),
	},
	itemRow: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: token('color.background.neutral'),
		},
	},
	unselectedOpacity: {
		opacity: token('opacity.disabled'),
	},
	loadingOverlay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '568px',
	},
	feedbackIconButton: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
});

// Estimated max height of the hover cards (in px) used for flip-up logic
const HOVER_CARD_HEIGHT = 160;
const HOVER_CARD_OFFSET = 8;

type HoverCardPosition = { top: number; left: number };

const useHoverCardPortal = (flipUp = false) => {
	const ref = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [position, setPosition] = useState<HoverCardPosition | null>(null);

	const show = () => {
		if (ref.current) {
			const rect = ref.current.getBoundingClientRect();
			const spaceBelow = window.innerHeight - rect.bottom;
			const top =
				!flipUp && spaceBelow > HOVER_CARD_HEIGHT
					? rect.bottom + HOVER_CARD_OFFSET
					: rect.top - HOVER_CARD_HEIGHT - HOVER_CARD_OFFSET;
			setPosition({ top, left: rect.left });
		}
		setIsVisible(true);
	};

	const hide = () => setIsVisible(false);

	return { ref, isVisible, position, show, hide };
};

type SuggestionItemRowProps = {
	suggestion: Suggestion;
	hasFeedbackBeenSubmitted?: boolean;
	onFeedbackSubmitted?: (suggestionId: string) => void;
	isSelected: boolean;
	spaceId?: string;
	spaceKey?: string;
	onToggleSelectItem: (id: string) => void;
	onHoverCardVisibilityChange: (isVisible: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
};

const SuggestionItemRow = ({
	spaceId,
	spaceKey,
	suggestion,
	isSelected,
	hasFeedbackBeenSubmitted,
	onFeedbackSubmitted,
	onToggleSelectItem,
	onHoverCardVisibilityChange,
	onMoveSuggestion,
}: SuggestionItemRowProps) => {
	const { formatMessage } = useIntl();
	const iconUrl = getSuggestionIconUrlFromContentUrl(suggestion.url);
	const { ref, isVisible, position, show, hide } = useHoverCardPortal();
	const { startPreviewAnalytics, cancelPreviewAnalytics } = useSuggestionPreviewAnalytics({
		spaceId,
		suggestion,
	});
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const isFeedbackModalOpenRef = useRef(false);
	const [isMovePopupOpen, setIsMovePopupOpen] = useState(false);
	const isMovePopupOpenRef = useRef(false);

	const handleShow = () => {
		if (isMovePopupOpenRef.current || isFeedbackModalOpenRef.current) return;
		show();
		onHoverCardVisibilityChange(true);
		startPreviewAnalytics();
	};

	const handleHide = () => {
		hide();
		onHoverCardVisibilityChange(false);
		cancelPreviewAnalytics();
	};

	const handleMovePopupOpenChange = (open: boolean) => {
		isMovePopupOpenRef.current = open;
		setIsMovePopupOpen(open);
		if (open) {
			handleHide();
		}
	};

	return (
		<Box
			ref={ref}
			xcss={cx(styles.itemRowContainer, isVisible && styles.itemRowContainerHovered)}
			onMouseEnter={handleShow}
			onMouseLeave={handleHide}
			onFocusCapture={handleShow}
			onBlur={(event) => {
				if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
				handleHide();
			}}
			testId={`review-content-suggestion-row-${suggestion.id}`}
		>
			<Flex alignItems="center" xcss={cx(styles.itemRow, !isSelected && styles.unselectedOpacity)}>
				<SelectToggleIcon
					isSelected={isSelected}
					onClick={() => onToggleSelectItem(suggestion.id)}
					ariaLabel={formatMessage(isSelected ? i18n.deselectItem : i18n.selectItem, {
						title: suggestion.title,
					})}
				/>
				<Box xcss={styles.suggestionLinkWrapper}>
					<Box xcss={styles.suggestionIconSlot} aria-hidden="true">
						{iconUrl ? (
							<FallbackableImage
								src={iconUrl}
								width="16"
								height="16"
								fallback={() => <DefaultAppIcon label="" />}
								role="presentation"
							/>
						) : (
							<DefaultAppIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
					<Anchor
						href={suggestion.url}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={formatMessage(i18n.openSuggestion, { title: suggestion.title })}
						xcss={styles.suggestionLink}
					>
						<Box xcss={styles.suggestionTitleWrapper}>
							<Text maxLines={1}>{suggestion.title}</Text>
						</Box>
					</Anchor>
				</Box>
				{!hasFeedbackBeenSubmitted &&
					expVal('cc-space-curation-feedback-survey', 'isEnabled', false) && (
						<Pressable
							xcss={styles.feedbackIconButton}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								isFeedbackModalOpenRef.current = true;
								handleHide();
								setIsFeedbackModalOpen(true);
							}}
							aria-label={formatMessage(i18n.feedbackButtonLabel, { title: suggestion.title })}
						>
							<Text size="small" color="color.text.subtle">
								{formatMessage(i18n.evalButtonLabel)}
							</Text>
						</Pressable>
					)}
				{(isVisible || isMovePopupOpen) && (
					<SuggestionMovePopup
						suggestionId={suggestion.id}
						spaceKey={spaceKey}
						isOpen={isMovePopupOpen}
						onOpenChange={handleMovePopupOpenChange}
						onMoveSuggestion={onMoveSuggestion}
						fallbackParents={suggestion.fallbackParents}
						currentParentId={suggestion.parentId}
					/>
				)}
			</Flex>
			<ModalTransition>
				{isFeedbackModalOpen && expVal('cc-space-curation-feedback-survey', 'isEnabled', false) && (
					<SuggestionFeedbackModal
						suggestion={suggestion}
						spaceId={spaceId}
						onClose={() => {
							isFeedbackModalOpenRef.current = false;
							setIsFeedbackModalOpen(false);
						}}
						onSubmit={() => onFeedbackSubmitted?.(suggestion.id)}
					/>
				)}
			</ModalTransition>
			{isVisible &&
				position &&
				createPortal(
					<Box
						xcss={cx(styles.portalHoverCard, isVisible && styles.portalHoverCardVisible)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ top: position.top, left: position.left }}
						testId="review-content-suggestion-hover-card-portal"
					>
						<SuggestionHoverCard suggestion={suggestion} />
					</Box>,
					document.body,
				)}
		</Box>
	);
};

type SuggestionGroupRowProps = {
	parentTitle: string;
	suggestions: Suggestion[];
	isExpanded: boolean;
	isGroupSelected: boolean;
	selectedItems: Set<string>;
	toggleExpand: (parentId: number) => void;
	parentId: number;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	onToggleSelectItem: (id: string) => void;
	onGroupHoverChange: (parentId: number, isHovering: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	spaceId?: string;
	submittedFeedbackIds: Set<string>;
	onFeedbackSubmitted: (suggestionId: string) => void;
	spaceKey?: string;
};

const SuggestionGroupRow = ({
	parentTitle,
	suggestions,
	isExpanded,
	isGroupSelected,
	selectedItems,
	toggleExpand,
	spaceId,
	spaceKey,
	parentId,
	toggleSelectGroup,
	onToggleSelectItem,
	onGroupHoverChange,
	submittedFeedbackIds,
	onFeedbackSubmitted,
	onMoveSuggestion,
}: SuggestionGroupRowProps) => {
	const { formatMessage } = useIntl();
	const [isHoverCardVisible, setIsHoverCardVisible] = useState(false);

	// value could change often and doesn't make sense to memoize
	// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render
	const selectedSuggestionCount = suggestions.filter((suggestion) =>
		selectedItems.has(suggestion.id),
	).length;

	return (
		<Box
			xcss={cx(
				styles.groupCard,
				isExpanded && styles.groupCardExpanded,
				isHoverCardVisible && styles.groupCardHovering,
			)}
			onMouseEnter={() => onGroupHoverChange(parentId, true)}
			onMouseLeave={() => onGroupHoverChange(parentId, false)}
			onFocusCapture={() => onGroupHoverChange(parentId, true)}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					onGroupHoverChange(parentId, false);
				}
			}}
			testId={`review-content-suggestion-group-card-${parentId}`}
		>
			<Flex xcss={!isGroupSelected && styles.unselectedOpacity}>
				<SelectToggleIcon
					isSelected={isGroupSelected}
					onClick={() => toggleSelectGroup(suggestions)}
					ariaLabel={formatMessage(isGroupSelected ? i18n.deselectGroup : i18n.selectGroup, {
						title: parentTitle,
					})}
				/>
				<Pressable
					xcss={styles.expandButton}
					onClick={() => toggleExpand(parentId)}
					aria-expanded={isExpanded}
					aria-label={formatMessage(isExpanded ? i18n.collapseGroup : i18n.expandGroup, {
						title: parentTitle,
					})}
				>
					<Box xcss={styles.groupTitleWrapper}>
						<Text weight="medium" maxLines={1}>
							{parentTitle}
						</Text>
					</Box>
					<Box xcss={styles.groupCountText}>
						<Text color="color.text.subtle">{selectedSuggestionCount}</Text>
					</Box>
					<Box xcss={styles.groupChevron}>
						{isExpanded ? (
							<ChevronUpIcon label="" color={token('color.icon.subtle')} />
						) : (
							<ChevronDownIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
				</Pressable>
			</Flex>
			<Box
				xcss={cx(styles.itemsListOuter, isExpanded && styles.itemsListOuterExpanded)}
				aria-hidden={!isExpanded}
			>
				<Box xcss={styles.itemsList}>
					{suggestions.map((suggestion) => (
						<SuggestionItemRow
							key={suggestion.id}
							suggestion={suggestion}
							isSelected={selectedItems.has(suggestion.id)}
							spaceId={spaceId}
							hasFeedbackBeenSubmitted={submittedFeedbackIds.has(suggestion.id)}
							onFeedbackSubmitted={onFeedbackSubmitted}
							spaceKey={spaceKey}
							onToggleSelectItem={onToggleSelectItem}
							onHoverCardVisibilityChange={setIsHoverCardVisible}
							onMoveSuggestion={onMoveSuggestion}
						/>
					))}
				</Box>
			</Box>
		</Box>
	);
};

type ModalLeftPanelProps = {
	groupedSuggestionsWithTitles: Map<number, ParentGrouping>;
	selectedItems: Set<string>;
	expandedGroups: Set<number>;
	toggleExpand: (parentId: number) => void;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	toggleSelectItem: (itemId: string) => void;
	onGroupHoverChange: (parentId: number, isHovering: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	spaceId?: string;
	spaceKey?: string;
};

const ModalLeftPanel = ({
	groupedSuggestionsWithTitles,
	selectedItems,
	expandedGroups,
	toggleExpand,
	toggleSelectGroup,
	toggleSelectItem,
	onGroupHoverChange,
	onMoveSuggestion,
	spaceId,
	spaceKey,
}: ModalLeftPanelProps) => {
	const { formatMessage } = useIntl();
	const [submittedFeedbackIds, setSubmittedFeedbackIds] = useState<Set<string>>(new Set());

	const handleFeedbackSubmitted = useCallback((suggestionId: string) => {
		setSubmittedFeedbackIds((prev) => new Set([...prev, suggestionId]));
	}, []);

	return (
		<Stack space="space.100">
			{[...groupedSuggestionsWithTitles.entries()].map(
				([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
					const isGroupSelected = groupSuggestions.some((s) => selectedItems.has(s.id));
					return (
						<SuggestionGroupRow
							key={parentId}
							parentTitle={parentTitle || formatMessage(i18n.untitled)}
							suggestions={groupSuggestions}
							isExpanded={expandedGroups.has(parentId)}
							isGroupSelected={isGroupSelected}
							selectedItems={selectedItems}
							toggleExpand={toggleExpand}
							parentId={parentId}
							toggleSelectGroup={toggleSelectGroup}
							onToggleSelectItem={toggleSelectItem}
							onGroupHoverChange={onGroupHoverChange}
							onMoveSuggestion={onMoveSuggestion}
							spaceId={spaceId}
							submittedFeedbackIds={submittedFeedbackIds}
							onFeedbackSubmitted={handleFeedbackSubmitted}
							spaceKey={spaceKey}
						/>
					);
				},
			)}
		</Stack>
	);
};

const PreviewFolderHeader = ({
	title,
	itemCount,
	isFolderExpanded,
}: {
	title: string;
	itemCount: number;
	isFolderExpanded: boolean;
}) => {
	const { ref, isVisible, position, show, hide } = useHoverCardPortal();

	return (
		<Box ref={ref} xcss={styles.previewFolderHeader} onMouseEnter={show} onMouseLeave={hide}>
			<Box xcss={styles.previewFolderTitleRow}>
				{isFolderExpanded ? (
					<FolderOpenIcon label="" color={token('color.icon.selected')} />
				) : (
					<FolderClosedIcon label="" color={token('color.icon.selected')} />
				)}
				<Box xcss={styles.previewGroupTitle}>
					<Text weight="medium" maxLines={1}>
						{title}
					</Text>
				</Box>
			</Box>
			{isVisible &&
				position &&
				createPortal(
					<Box
						xcss={cx(styles.portalHoverCard, isVisible && styles.portalHoverCardVisible)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ top: position.top, left: position.left }}
					>
						<GroupHoverCard title={title} itemCount={itemCount} />
					</Box>,
					document.body,
				)}
		</Box>
	);
};

type PreviewSuggestionRowProps = {
	suggestion: Suggestion;
	spaceId?: string;
};

const PreviewSuggestionRow = ({ suggestion, spaceId }: PreviewSuggestionRowProps) => {
	const { ref, isVisible, position, show, hide } = useHoverCardPortal();
	const iconUrl = getSuggestionIconUrlFromContentUrl(suggestion.url);
	const { startPreviewAnalytics, cancelPreviewAnalytics } = useSuggestionPreviewAnalytics({
		spaceId,
		suggestion,
	});

	const handleShow = () => {
		show();
		startPreviewAnalytics();
	};

	const handleHide = () => {
		hide();
		cancelPreviewAnalytics();
	};

	return (
		<Box
			ref={ref}
			xcss={cx(styles.itemRowContainer, isVisible && styles.itemRowContainerHovered)}
			onMouseEnter={handleShow}
			onMouseLeave={handleHide}
			onFocusCapture={handleShow}
			onBlur={handleHide}
			testId={`review-content-preview-suggestion-row-${suggestion.id}`}
		>
			<Flex alignItems="center" xcss={styles.previewSuggestionRow}>
				<Box xcss={styles.suggestionIconSlot} aria-hidden="true">
					{iconUrl ? (
						<FallbackableImage
							src={iconUrl}
							width="16"
							height="16"
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					) : (
						<DefaultAppIcon label="" color={token('color.icon.subtle')} />
					)}
				</Box>
				<Box xcss={styles.previewSuggestionTitle}>
					<Text maxLines={1}>{suggestion.title}</Text>
				</Box>
			</Flex>
			{isVisible &&
				position &&
				createPortal(
					<Box
						xcss={cx(styles.portalHoverCard, isVisible && styles.portalHoverCardVisible)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ top: position.top, left: position.left }}
					>
						<SuggestionHoverCard suggestion={suggestion} />
					</Box>,
					document.body,
				)}
		</Box>
	);
};

type ParentGrouping = {
	parentTitle: string;
	suggestions: Suggestion[];
};

type ModalRightPanelProps = {
	groupedSuggestionsWithTitles: Map<number, ParentGrouping>;
	selectedItems: Set<string>;
	previewParentId: number | undefined;
	isHoveringGroupList: boolean;
	hoveredParentId: number | null;
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
};

const ModalRightPanel = ({
	groupedSuggestionsWithTitles,
	selectedItems,
	previewParentId,
	isHoveringGroupList,
	hoveredParentId,
	spaceName,
	spaceId,
	iconPath,
}: ModalRightPanelProps) => {
	const { formatMessage } = useIntl();
	const resolvedSpaceName = spaceName || formatMessage(i18n.untitled);
	const spaceAvatarAlt = formatMessage(i18n.spaceAvatarAlt, { spaceName: resolvedSpaceName });

	return (
		<Box xcss={styles.rightPanel}>
			<Box xcss={styles.closeButtonWrapper}>
				<ModalHeader hasCloseButton />
			</Box>
			<Box xcss={styles.container}>
				<Flex xcss={styles.spaceHeader}>
					<Box xcss={styles.spaceAvatarWrapper}>
						<FallbackableImage
							src={iconPath || ''}
							width="24"
							height="24"
							alt={spaceAvatarAlt}
							fallback={() => <DefaultAppIcon label={spaceAvatarAlt} />}
						/>
					</Box>
					<Box xcss={styles.spaceHeaderTitle}>
						<Text weight="medium">{resolvedSpaceName}</Text>
					</Box>
				</Flex>
				<Box xcss={styles.previewPanelContent}>
					<Box xcss={styles.previewCard}>
						<Stack space="space.100" xcss={styles.previewFoldersList}>
							<Stack
								xcss={styles.previewPlaceholderSection}
								testId="review-content-suggestions-modal-preview-placeholder-section"
							>
								<Flex xcss={styles.previewPlaceholderRow}>
									<Box xcss={styles.previewPlaceholderDot} />
									<Box xcss={styles.previewPlaceholderBarLong} />
								</Flex>
								<Box xcss={styles.previewPlaceholderBarFull} />
							</Stack>
							<Box xcss={styles.eyelashWrapper}>
								{isHoveringGroupList && hoveredParentId !== null ? (
									<img src={eyelashIcon} alt="" aria-hidden="true" css={styles.eyelash} />
								) : null}
								<Box
									xcss={styles.previewGroupsSection}
									testId="review-content-suggestions-modal-preview-groups-section"
								>
									{[...groupedSuggestionsWithTitles.entries()]
										.filter(([, { suggestions: groupSuggestions }]) =>
											groupSuggestions.some((s) => selectedItems.has(s.id)),
										)
										.map(([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
											const isFolderExpanded = previewParentId === parentId;
											return (
												<Box key={parentId} xcss={cx(styles.previewFolderPreview)}>
													<PreviewFolderHeader
														title={parentTitle || formatMessage(i18n.untitled)}
														itemCount={groupSuggestions.length}
														isFolderExpanded={isFolderExpanded}
													/>
													<Box
														xcss={cx(
															styles.previewFolderBody,
															styles.previewFolderBodyIndented,
															isFolderExpanded && styles.previewFolderBodyExpanded,
														)}
													>
														<Stack space="space.050" xcss={styles.previewSuggestionList}>
															{groupSuggestions
																.filter((s) => selectedItems.has(s.id))
																.slice(0, PREVIEW_ITEM_COUNT)
																.map((suggestion) => (
																	<PreviewSuggestionRow
																		key={suggestion.id}
																		suggestion={suggestion}
																		spaceId={spaceId}
																	/>
																))}
														</Stack>
													</Box>
												</Box>
											);
										})}
								</Box>
							</Box>
							<Stack
								xcss={styles.previewPlaceholderSection}
								testId="review-content-suggestions-modal-preview-placeholder-section"
							>
								<Flex xcss={styles.previewPlaceholderRow}>
									<Box xcss={styles.previewPlaceholderDot} />
									<Box xcss={styles.previewPlaceholderBarLong} />
								</Flex>
								<Box xcss={styles.previewPlaceholderBarFull} />
								<Flex xcss={styles.previewPlaceholderRow}>
									<Box xcss={styles.previewPlaceholderDot} />
									<Box xcss={styles.previewPlaceholderBarLong} />
								</Flex>
								<Box xcss={styles.previewPlaceholderBarFull} />
								<Flex xcss={styles.previewPlaceholderRow}>
									<Box xcss={styles.previewPlaceholderDot} />
									<Box xcss={styles.previewPlaceholderBarLong} />
								</Flex>
								<Box xcss={styles.previewPlaceholderBarFull} />
								<Flex xcss={styles.previewPlaceholderRow}>
									<Box xcss={styles.previewPlaceholderDot} />
									<Box xcss={styles.previewPlaceholderBarLong} />
								</Flex>
								<Box xcss={styles.previewPlaceholderBarFull} />
								<Flex xcss={styles.previewPlaceholderRow}>
									<Box xcss={styles.previewPlaceholderDot} />
									<Box xcss={styles.previewPlaceholderBarLong} />
								</Flex>
								<Box xcss={styles.previewPlaceholderBarFull} />
							</Stack>
						</Stack>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

type ReviewContentSuggestionsModalInnerProps = ReviewContentSuggestionsModalProps & {
	flags: FlagsStateContainer;
};

export const ReviewContentSuggestionsModalInner = ({
	groups,
	spaceKey,
	spaceName,
	spaceId,
	iconPath,
	onClose,
	onSaveSuccess,
	flags,
}: ReviewContentSuggestionsModalInnerProps) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const [suggestionOverrides, setSuggestionOverrides] = useState<Map<string, { parentId: number }>>(
		() => new Map(),
	);

	const allSuggestions = useMemo(
		() =>
			groups
				.flatMap((g) => g.remoteObjects)
				.map((s) => {
					const override = suggestionOverrides.get(s.id);
					return override ? { ...s, ...override } : s;
				}),
		[groups, suggestionOverrides],
	);

	const stackedIconUrls = useMemo(
		() =>
			Array.from(
				new Set(
					allSuggestions.map((s) => getSuggestionIconUrlFromContentUrl(s.url)).filter(Boolean),
				),
			),
		[allSuggestions],
	);

	const [expandedGroups, setExpandedGroups] = useState<Set<number>>(() => new Set());
	const [selectedItems, setSelectedItems] = useState<Set<string>>(
		() => new Set(allSuggestions.map((s) => s.id)),
	);
	const [isHoveringGroupList, setIsHoveringGroupList] = useState(false);
	const [hoveredParentId, setHoveredParentId] = useState<number | null>(null);
	const [previewParentId, setPreviewParentId] = useState<number | undefined>(groups[0]?.parentId);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const parentIds = useMemo(() => {
		const overriddenIds = [...suggestionOverrides.values()].map((o) => String(o.parentId));
		return [...new Set([...groups.map((g) => String(g.parentId)), ...overriddenIds])];
	}, [groups, suggestionOverrides]);

	const { titlesByContentId, isLoading: isLoadingTitles } = useContentTitles(parentIds);
	const { submitActions } = useSubmitSuggestionActions({ spaceKey, flags });
	const { cloudId } = useSessionData();
	const {
		unauthenticatedServices: thirdPartyUnauthenticatedServices,
		isLoading: isLoadingThirdPartyAuth,
		refetch: refetchThirdPartyAuth,
	} = useFetch3PAuthentications({
		products: ALL_EXTERNAL_PRODUCT_CONNECTIONS,
		cloudId: cloudId ?? '',
	});

	const isLoading = isLoadingTitles || isSubmitting || isLoadingThirdPartyAuth;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const openTimestampRef = useRef(Date.now());
	const dismissMethodRef = useRef<'close_button' | 'escape'>('close_button');

	const sourceTypes = useMemo(
		() =>
			Array.from(new Set(allSuggestions.map((s) => getSuggestionProductKeyFromContentUrl(s.url)))),
		[allSuggestions],
	);

	const mountValuesRef = useRef({
		spaceId,
		suggestionCount: allSuggestions.length,
		sourceTypes,
		renderedSuggestions: allSuggestions.map((s, rankIndex) => ({
			suggestionId: s.id,
			sourceType: getSuggestionProductKeyFromContentUrl(s.url),
			rankIndex,
			suggestedParentId: s.parentId,
		})),
		createAnalyticsEvent,
	});

	useEffect(() => {
		const {
			spaceId: si,
			suggestionCount: sc,
			sourceTypes: st,
			renderedSuggestions: rs,
			createAnalyticsEvent: cae,
		} = mountValuesRef.current;
		cae({
			type: 'sendScreenEvent',
			data: {
				name: 'curationModule',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					suggestionCount: sc,
					sourceTypes: st,
				},
			},
		}).fire();
		cae({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'suggestions',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					suggestions: rs,
				},
			},
		}).fire();
	}, []);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				dismissMethodRef.current = 'escape';
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	const handleClose = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationModule',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					suggestionCountAtDismiss: allSuggestions.length,
					dismissMethod: dismissMethodRef.current,
					timeInModuleMs: Date.now() - openTimestampRef.current,
				},
			},
		}).fire();
		dismissMethodRef.current = 'close_button';
		onClose();
	};

	const handleAdd = async () => {
		setIsSubmitting(true);
		try {
			const ok = await submitActions(allSuggestions, selectedItems);
			if (ok) {
				const suggestionAcceptancePayload = (s: Suggestion) => ({
					suggestionId: s.id,
					sourceType: getSuggestionProductKeyFromContentUrl(s.url),
					targetFolderId: s.parentId,
					folderWasRecommended: true,
				});
				// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
				const acceptedSuggestions = allSuggestions
					.filter((s) => selectedItems.has(s.id))
					.map(suggestionAcceptancePayload);
				// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
				const dismissedSuggestions = allSuggestions
					.filter((s) => !selectedItems.has(s.id))
					.map(suggestionAcceptancePayload);
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'accepted',
						actionSubject: 'suggestions',
						source: 'containerNavigation',
						attributes: {
							spaceId,
							count: acceptedSuggestions.length,
							acceptedSuggestions,
							dismissedSuggestions,
						},
					},
				}).fire();
				await onSaveSuccess();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleMoveSuggestion = useCallback((suggestionId: string, newParentId: number) => {
		setSuggestionOverrides((prev) => new Map(prev).set(suggestionId, { parentId: newParentId }));
		setExpandedGroups((prev) => new Set(prev).add(newParentId));
		setPreviewParentId(newParentId);
	}, []);

	const groupedSuggestionsWithTitles = useMemo(() => {
		const originalGroups = new Map<number, ParentGrouping>();
		const newGroups = new Map<number, ParentGrouping>();
		const originalParentIds = new Set(groups.map((g) => g.parentId));

		allSuggestions.forEach((suggestion) => {
			const pid = suggestion.parentId;
			const isNewGroup = !originalParentIds.has(pid);
			const target = isNewGroup ? newGroups : originalGroups;
			const existing = target.get(pid);
			if (existing) {
				target.set(pid, { ...existing, suggestions: [...existing.suggestions, suggestion] });
			} else {
				target.set(pid, {
					parentTitle: titlesByContentId.get(String(pid)) ?? '',
					suggestions: [suggestion],
				});
			}
		});

		return new Map([...originalGroups.entries(), ...newGroups.entries()]);
	}, [allSuggestions, titlesByContentId, groups]);

	useEffect(() => {
		if (previewParentId === undefined || groups.length === 0) {
			setPreviewParentId(groups[0]?.parentId);
			return;
		}

		if (!groupedSuggestionsWithTitles.has(previewParentId)) {
			setPreviewParentId(groups[0]?.parentId);
		}
	}, [groups, previewParentId, groupedSuggestionsWithTitles]);

	const handleGroupHoverChange = (parentId: number, isHovering: boolean) => {
		setIsHoveringGroupList(isHovering);
		setHoveredParentId(isHovering ? parentId : null);

		if (isHovering) {
			setPreviewParentId(parentId);
		}
	};

	const toggleExpand = (parentId: number) => {
		setExpandedGroups((prev) => {
			const next = new Set(prev);
			if (next.has(parentId)) {
				next.delete(parentId);
			} else {
				next.add(parentId);
			}
			return next;
		});
	};

	const toggleSelectItem = (itemId: string) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	const toggleSelectGroup = (groupSuggestions: Suggestion[]) => {
		const allSelected = groupSuggestions.every((s) => selectedItems.has(s.id));
		setSelectedItems((prev) => {
			const next = new Set(prev);
			groupSuggestions.forEach((s) => {
				if (allSelected) {
					next.delete(s.id);
				} else {
					next.add(s.id);
				}
			});
			return next;
		});
	};

	return (
		<Modal onClose={handleClose} width="x-large">
			{isLoading ? (
				<Box xcss={styles.loadingOverlay} testId="review-content-suggestions-modal-loading">
					<Spinner
						size="large"
						label={isSubmitting ? formatMessage(i18n.submitting) : formatMessage(i18n.loading)}
					/>
				</Box>
			) : (
				<Flex xcss={styles.layout}>
					<Box xcss={styles.leftPanel}>
						<Stack space="space.100">
							<Box xcss={styles.stackedContentTypeIconWrapper}>
								<StackedContentTypeIcons iconUrls={stackedIconUrls} variant="large" tilted />
							</Box>
							<Heading size="medium">{formatMessage(i18n.bringTogetherTitle)}</Heading>
							<Text color="color.text.subtle">{formatMessage(i18n.bringTogetherDescription)}</Text>
						</Stack>
						<Box xcss={styles.groupListScrollArea}>
							<Stack space="space.100">
								<ModalLeftPanel
									groupedSuggestionsWithTitles={groupedSuggestionsWithTitles}
									selectedItems={selectedItems}
									expandedGroups={expandedGroups}
									toggleExpand={toggleExpand}
									toggleSelectGroup={toggleSelectGroup}
									toggleSelectItem={toggleSelectItem}
									onGroupHoverChange={handleGroupHoverChange}
									onMoveSuggestion={handleMoveSuggestion}
									spaceId={spaceId}
									spaceKey={spaceKey}
								/>
								<ConnectAppsForMoreSuggestionsCard
									unauthenticatedServices={thirdPartyUnauthenticatedServices}
									onThirdPartyAuthRefetch={refetchThirdPartyAuth}
									spaceName={spaceName}
									iconPath={iconPath}
								/>
							</Stack>
						</Box>
						<Flex justifyContent="end" xcss={styles.addButtonRow}>
							<Button
								appearance="primary"
								onClick={() => void handleAdd()}
								isDisabled={selectedItems.size === 0}
							>
								{formatMessage(i18n.addItemsToSpace, { count: selectedItems.size })}
							</Button>
						</Flex>
					</Box>
					<ModalRightPanel
						groupedSuggestionsWithTitles={groupedSuggestionsWithTitles}
						selectedItems={selectedItems}
						previewParentId={previewParentId}
						isHoveringGroupList={isHoveringGroupList}
						hoveredParentId={hoveredParentId}
						spaceName={spaceName}
						spaceId={spaceId}
						iconPath={iconPath}
					/>
				</Flex>
			)}
		</Modal>
	);
};

type ReviewContentSuggestionsModalProps = {
	groups: SuggestionGroup[];
	spaceKey?: string;
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
	onClose: () => void;
	onSaveSuccess: () => void | Promise<void>;
};

export const ReviewContentSuggestionsModal = (props: ReviewContentSuggestionsModalProps) => (
	<Subscribe to={[FlagsStateContainer]}>
		{(flags: FlagsStateContainer) => (
			<ReviewContentSuggestionsModalInner {...props} flags={flags} />
		)}
	</Subscribe>
);
